// Renamed from UserGroupConfig.tsx
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  getUserGroups,
  getUsers,
  addUserGroup,
  updateUserGroup,
  deleteUserGroup,
  addPermission,
  deletePermission,
  getDocumentTypePermissionsByGroup,
  getDocumentTypes
} from '../api';
import { getPermissionTemplates, ApiPermissionTemplate } from '@api/permissionTemplates';

/**
 * State for the group form
 */
interface GroupFormState {
  groupName: string;
  groupDescription: string;
  groupIsDomain: boolean;
  userIds: number[];
  documentTypeIds: number[];
}

const emptyGroup: GroupFormState = {
  groupName: '',
  groupDescription: '',
  groupIsDomain: false,
  userIds: [],
  documentTypeIds: []
};
const OBJECTTYPE_DOCUMENTTYPE = 2;

/**
 * UserGroupAdmin component for managing user groups and their permissions
 */
export default function UserGroupAdmin() {
  // State hooks
  const [groups, setGroups] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [docTypes, setDocTypes] = useState<any[]>([]);
  const [groupPermissions, setGroupPermissions] = useState<any[]>([]);
  const [permissionTemplates, setPermissionTemplates] = useState<ApiPermissionTemplate[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<GroupFormState>(emptyGroup);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterAvail, setFilterAvail] = useState('');
  const [filterMembers, setFilterMembers] = useState('');
  const [filterDocAvail, setFilterDocAvail] = useState('');
  const [filterDocAssigned, setFilterDocAssigned] = useState('');

  // Initial data load
  useEffect(() => {
    initialLoad();
  }, []);

  async function initialLoad() {
    setLoading(true);
    setError(null);
    try {
      const [g, u, dt, tmpl] = await Promise.all([
        getUserGroups(),
        getUsers(),
        getDocumentTypes(),
        getPermissionTemplates()
      ]);
      setGroups(g);
      setUsers(u);
      setDocTypes(dt || []);
      setPermissionTemplates(tmpl || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchGroupPermissions(groupId: number) {
    try {
      const perms = await getDocumentTypePermissionsByGroup(groupId);
      console.log('Fetched group permissions:', perms); // Log permissions to console
      setGroupPermissions(perms);
      return perms;
    } catch {
      setGroupPermissions([]);
      return [];
    }
  }

  function pickBestPermissionTemplate(): number | null {
    if (!permissionTemplates.length) return null;
    const full = permissionTemplates.find(t => (t.fullControl ?? (t as any).FullControl) === true);
    if (full) return full.id;
    let best: ApiPermissionTemplate | undefined;
    let bestScore = -1;
    permissionTemplates.forEach(t => {
      const score =
        (t.canRead || (t as any).CanRead ? 1 : 0) +
        (t.canWrite || (t as any).CanWrite ? 1 : 0) +
        (t.canModify || (t as any).CanModify ? 1 : 0) +
        (t.canDelete || (t as any).CanDelete ? 1 : 0);
      if (score > bestScore) {
        bestScore = score;
        best = t;
      }
    });
    return best ? best.id : null;
  }

  const selectedGroup = groups.find((g: any) => g.id === selectedGroupId) || null;

  /**
   * Initialize the form for a selected group
   */
  async function initFormForGroup(group: any | null) {
    if (!group) {
      setForm(emptyGroup);
      setGroupPermissions([]);
      return;
    }
    const perms = await fetchGroupPermissions(group.id);
    // Ensure IDs are numbers when determining membership
    const memberIds = users
      .filter(u => Array.isArray(u.userGroups) && u.userGroups.some((gid: any) => Number(gid) === Number(group.id)))
      .map(u => Number(u.id));
    // Ensure document type ids from permissions are numbers
    const docTypeIds = perms
      .filter((p: any) => Number(p.objectTypeId) === OBJECTTYPE_DOCUMENTTYPE)
      .map((p: any) => Number(p.objectId));
    setForm({
      groupName: group.groupName || '',
      groupDescription: group.groupDescription || '',
      groupIsDomain: !!group.groupIsDomain,
      userIds: memberIds,
      documentTypeIds: docTypeIds
    });
    resetSelections();
  }

  function resetSelections() {
    setFilterAvail('');
    setFilterMembers('');
    setFilterDocAvail('');
    setFilterDocAssigned('');
  }

  function handleSelect(id: number) {
    setSelectedGroupId(id);
    setCreating(false);
    setEditing(false);
    initFormForGroup(groups.find((g: any) => g.id === id) || null);
  }

  function handleAdd() {
    setSelectedGroupId(null);
    setCreating(true);
    setEditing(true);
    initFormForGroup(null);
  }

  function handleEdit() {
    if (selectedGroup) setEditing(true);
  }

  async function handleDelete() {
    if (selectedGroupId == null) return;
    if (!window.confirm('Delete this group?')) return;
    setLoading(true);
    setError(null);
    try {
      await deleteUserGroup(selectedGroupId);
      await initialLoad();
      setSelectedGroupId(null);
      setCreating(false);
      setEditing(false);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function handleFormChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value } as any));
  }

  async function handleSave() {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        groupName: form.groupName,
        groupDescription: form.groupDescription,
        groupIsDomain: form.groupIsDomain
      } as any;
      let groupId = selectedGroupId;
      if (groupId == null) {
        const newGroup = await addUserGroup(payload);
        groupId = newGroup.id;
        await syncMembership(groupId, [], form.userIds);
        // removed syncMembership; only sync document types
        await syncDocumentTypes(groupId, [], form.documentTypeIds);
      } else {
        // Refresh permissions to avoid using stale state
        const latestPerms = await fetchGroupPermissions(groupId);
        // Ensure all ID comparisons are number-safe
        const beforeUsers = users
          .filter(u => Array.isArray(u.userGroups) && u.userGroups.some((gid: any) => Number(gid) === Number(groupId!)))
          .map(u => Number(u.id));
        const beforeDocTypes = (latestPerms || [])
          .filter((p: any) => Number(p.objectTypeId) === OBJECTTYPE_DOCUMENTTYPE)
          .map((p: any) => Number(p.objectId));
        await updateUserGroup(groupId, payload);
        await Promise.all([
          syncMembership(groupId, beforeUsers, form.userIds.map(Number)),
          syncDocumentTypes(groupId, beforeDocTypes, form.documentTypeIds.map(Number))
        ]);
        await syncDocumentTypes(groupId, beforeDocTypes, form.documentTypeIds.map(Number));
      }
      await initialLoad();
      setSelectedGroupId(groupId!);
      setCreating(false);
      setEditing(false);
      await fetchGroupPermissions(groupId!);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Synchronize group membership with backend
   */
  async function syncMembership(groupId: number, oldMembers: number[], newMembers: number[]) {
    const toAdd = newMembers.filter(id => !oldMembers.includes(id));
    const toRemove = oldMembers.filter(id => !newMembers.includes(id));
    const impacted = users.filter(u => toAdd.includes(u.id) || toRemove.includes(u.id));
    for (const u of impacted) {
      const current = new Set(u.userGroups || []);
      if (toAdd.includes(u.id)) current.add(groupId);
      if (toRemove.includes(u.id)) current.delete(groupId);
      const updated = { ...u, userGroups: Array.from(current) };
      await fetch(`/api/user/${u.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated)
      });
    }
  }

  /**
   * Synchronize document type permissions with backend
   */
  async function syncDocumentTypes(groupId: number, oldDocTypes: number[], newDocTypes: number[]) {
    const toAdd = newDocTypes.filter(id => !oldDocTypes.includes(Number(id))).map(Number);
    const toRemove = oldDocTypes.filter(id => !newDocTypes.includes(Number(id))).map(Number);
    const bestTemplateId = pickBestPermissionTemplate();

    // Add permissions for new document types
    for (const dtId of toAdd) {
      await addPermission({ groupId, objectId: dtId, objectTypeId: OBJECTTYPE_DOCUMENTTYPE, permissionTemplateId: bestTemplateId ?? 0 });
    }

    // Refresh permissions before deletions to avoid stale state
    const latestPerms = await fetchGroupPermissions(groupId);

    // Delete permissions for removed document types
    for (const dtId of toRemove) {
  const perm = (latestPerms || []).find((p: any) => Number(p.groupId) === Number(groupId) && Number(p.objectTypeId) === OBJECTTYPE_DOCUMENTTYPE && Number(p.objectId) === Number(dtId));
      if (perm) await deletePermission(perm.id);
    }

    // Final refresh
    await fetchGroupPermissions(groupId);
  }

  // Sort groups by name
  const sortedGroups = React.useMemo(() => {
    return [...groups].sort((a, b) => (a.groupName || '').localeCompare(b.groupName || '', undefined, { sensitivity: 'base' }));
  }, [groups]);

  // Re-init form when users or selected group change to ensure checkboxes reflect membership
  // Re-init form when users or selected group change
  useEffect(() => {
    if (selectedGroupId !== null) {
      const grp = groups.find(g => g.id === selectedGroupId) || null;
      if (grp) initFormForGroup(grp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, selectedGroupId]);

  // Render
  return (
    <div className="adm-shell adm-shell--tall">
      {/* Group list */}
      <div className="adm-master adm-master--wide" style={{ maxWidth: 800 }}>
        <div className="adm-master-head">
          <h3>Groups</h3>
          <button onClick={handleAdd} title='Add group' className="adm-add-btn">
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><line x1='12' y1='5' x2='12' y2='19'/><line x1='5' y1='12' x2='19' y2='12'/></svg>
          </button>
        </div>
        {loading && <div className="adm-status">Loading...</div>}
        {error && <div className="adm-status adm-status--error">{error}</div>}
        <div className="adm-master-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Group</th>
              </tr>
            </thead>
            <tbody>
              {sortedGroups.map(g => {
                const sel = g.id === selectedGroupId;
                const textStyle: React.CSSProperties = { fontWeight: g.groupIsDomain ? 700 : 400 };
                return (
                  <tr key={g.id} onClick={() => handleSelect(g.id)} className={`adm-row${sel ? ' is-selected' : ''}`}>
                    <td style={textStyle}>{g.groupName}</td>
                  </tr>
                );
              })}
              {sortedGroups.length === 0 && !loading && (
                <tr><td colSpan={2} className="adm-empty">No groups</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Group details and edit form */}
      <div className="adm-detail">
      {selectedGroupId === null && !creating ? (
        <div className="adm-placeholder">
          Select a group from the list or click the + icon to add a new group.
        </div>
      ) : (
        <div style={{ maxWidth: 800 }}>
          <h3 style={{ marginTop: 0 }}>{selectedGroupId == null ? 'Add Group' : 'Edit Group'}</h3>
          <form onSubmit={e => { e.preventDefault(); handleSave(); }} style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'relative' }}>
            <div className="adm-card" style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 12 }}>
              <label className="adm-field-label">Name</label>
              <input name='groupName' value={form.groupName} onChange={handleFormChange} required className="adm-input" />

              <label className="adm-field-label">Description</label>
              <input name='groupDescription' value={form.groupDescription} onChange={handleFormChange} className="adm-input" />

              <label className="adm-field-label">Domain Group</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type='checkbox' name='groupIsDomain' checked={form.groupIsDomain} onChange={handleFormChange} style={{ zoom: 1.2 }} />
                <span className="adm-hint">Membership may be auto-managed</span>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24 }}>
              {/* Document types checklist */}
              <div className="adm-card">
                <h4 style={{ margin: '0 0 12px 0' }}>Document Types</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {docTypes.map(dt => {
                    const dtId = Number(dt.id);
                    const isChecked = form.documentTypeIds.includes(dtId);
                    return (
                      <label key={dt.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, background: isChecked ? 'var(--sc-surface-head)' : 'transparent', borderRadius: 4, padding: '2px 6px' }}>
                        <input
                          type='checkbox'
                          checked={isChecked}
                          onChange={() => {
                            setForm(prev => ({
                              ...prev,
                              documentTypeIds: isChecked
                                ? prev.documentTypeIds.filter(id => id !== dtId)
                                : [...prev.documentTypeIds, dtId]
                            }));
                          }}
                        />
                        {dt.documentTypeName}
                      </label>
                    );
                  })}
                  {docTypes.length === 0 && <div style={{ color: 'var(--sc-text-muted)', padding: 8 }}>No document types</div>}
                </div>
              </div>
            </div>
            {/* Action buttons */}
            <div className="adm-actions adm-actions--spread">
              <div className="adm-actions-group">
                <button type='submit' className="adm-btn adm-btn-primary" disabled={loading}>Save</button>
                <button
                  type='button'
                  className="adm-btn adm-btn-secondary"
                  onClick={() => {
                    setEditing(false);
                    if (selectedGroupId !== null) initFormForGroup(groups.find((g: any) => g.id === selectedGroupId) || null);
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
              {selectedGroupId !== null && (
                <button type='button' className="adm-btn adm-btn-danger" onClick={handleDelete} disabled={loading}>Delete</button>
              )}
            </div>
          </form>
        </div>
      )}
      </div>
    </div>
  );
}
